import React, { type FC, Fragment, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import Box from '../box/box';
import Heading from '../heading/heading';
import Link from '../link/link';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Text from '../text/text';
import VisibleLottie from '../lottie/visible-lottie';
import type { BaseCarouselProps, BaseCarouselMobileImageProps } from './carousel';

export interface CarouselMobileProps extends BaseCarouselProps, BaseCarouselMobileImageProps {
  /** Allows the component to get additional styles. */
  className?: string;
}

const CarouselMobile: FC<PropsWithChildren<CarouselMobileProps>> = ({
  className,
  cta,
  items,
  shouldOverflowMobileImages,
  textUnderCarousel,
  ...rest
}) => (
  <Box
    {...rest}
    css={{
      label: 'mobileCarousel',
      flex: '0 1 100%',
      flexDirection: 'column',
    }}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    {items.map(item => {
      const { heading, media, text } = item;
      const mediaType = media?.type?.split('/')[0];
      const hasAnimation = mediaType === 'application/json';

      return (
        <Fragment key={item.id}>
          <Box
            css={theme => ({
              label: 'carouselMobile',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: theme.space.s24,
              marginTop: theme.space.s80,
              '&:first-of-type': {
                marginTop: theme.space.s0,
              },
            })}
          >
            <Heading
              as="h5"
              css={theme => [
                {
                  marginBottom: theme.space.s0,
                  textAlign: 'left',
                },
              ]}
              variant="headingXL"
            >
              {heading}
            </Heading>
            {!textUnderCarousel && text && (
              <Text
                css={theme => ({
                  marginTop: theme.space.s32,
                  marginBottom: theme.space.s0,
                })}
                variant="paragraphS400"
              >
                {text}
              </Text>
            )}
          </Box>
          {hasAnimation ? (
            <Box
              css={theme => [
                {
                  backgroundColor: theme.colors.coolGrey[0],
                  marginBottom: theme.space.s16,
                  height: '100%',
                  minHeight: '375px',
                  [theme.breakpoints.bp500.min]: {
                    minHeight: '475px',
                  },
                },
                shouldOverflowMobileImages && {
                  width: '200%',
                  transform: 'translateX(-25%)',
                },
              ]}
            >
              <VisibleLottie
                willLoop
                alt={media?.translations?.[0]?.alt || media?.title || ''}
                css={{
                  backgroundSize: 'cover',
                }}
                path={withImage(media)}
              />
            </Box>
          ) : (
            <ImageWithPlaceholder
              alt={media?.translations?.[0]?.alt || media?.title || ''}
              css={theme => ({
                objectFit: 'contain',
                width: '100%',
                left: 0,
                position: 'relative',
                maxWidth: 'unset',
                paddingBottom: theme.space.s16,
                height: '100%',
                minHeight: '375px',
                [theme.breakpoints.bp500.min]: {
                  minHeight: '475px',
                },
              })}
              src={withImage(media)}
            />
          )}
          {textUnderCarousel && text && (
            <Text
              css={theme => ({
                marginBottom: theme.space.s0,
                textAlign: 'left',
              })}
              variant="paragraphS400"
            >
              {text}
            </Text>
          )}
        </Fragment>
      );
    })}
    {cta && (
      <Box
        css={theme => ({
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.space.s80,
        })}
      >
        {cta.final_url && (
          <Link displayAsButton href={cta.final_url}>
            {cta.default_link_text}
          </Link>
        )}
      </Box>
    )}
  </Box>
);

export { CarouselMobile };
export default CarouselMobile;
